import React from 'react';
import PropTypes from 'prop-types';

import {
    ResponsiveContainer,
    BarChart,
    Bar,
    Legend,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
} from 'recharts';

import GraphTitle from 'app/shared/components/Graphs/GraphTitle';
import GraphEmptyMessage from 'app/shared/components/Graphs/GraphEmptyMessage';
import GraphTotalTooltip from 'app/shared/components/Graphs/GraphTotalTooltip';
import SpinnerContainer from 'app/shared/components/SpinnerContainer';

import GraphConstants from 'app/shared/constants/GraphConstants';

import numberUtils from 'app/shared/utils/numberUtils';

// Stylesheets
import 'app/shared/components/Graphs/style.scss';

const propTypes = {
    brand: PropTypes.string.isRequired,
    data: PropTypes.object.isRequired,
    isLoading: PropTypes.bool,
};

const defaultProps = {
    isLoading: false,
};

const GRAPH = GraphConstants.GRAPHS.INVOICE;
const barOneKey = GraphConstants.GRAPH_DATA_TYPES.CORE_PRODUCTS;
const barTwoKey = GraphConstants.GRAPH_DATA_TYPES.BOOST;
const barThreeKey = GraphConstants.GRAPH_DATA_TYPES.ROCKETSHIP;

const renderFormatter = value => numberUtils.asCurrency(value);

const InvoiceGraph = ({
    brand,
    isLoading,
    data,
}) => {
    const configs = GRAPH.default;
    const currentData = isLoading ? 0 : data[configs.DATA_TYPE];

    const coreTotal = parseFloat(currentData[barOneKey] || 0);
    const boostTotal = parseFloat(currentData[barTwoKey] || 0);
    const rocketshipTotal = parseFloat(currentData[barThreeKey] || 0);

    const renderGraph = () => (
        <div className="Graph-container">
            {currentData.isEmpty && <GraphEmptyMessage message="No invoices available" />}
            <ResponsiveContainer width="100%" height={300}>
                <BarChart data={currentData.months} key={barOneKey}>
                    <XAxis dataKey="name" />
                    <YAxis />
                    <CartesianGrid strokeDasharray="3 3" vertical={false} />
                    <Tooltip content={<GraphTotalTooltip />} formatter={renderFormatter} />
                    <Legend align="left" verticalAlign="bottom" wrapperStyle={{ marginLeft: 60 }} />
                    {coreTotal && (
                        <Bar
                            dataKey={barOneKey}
                            stackId="a"
                            fill={GraphConstants.COLORS[brand].primary}
                        />
                    )}
                    {boostTotal && (
                        <Bar
                            dataKey={barTwoKey}
                            stackId="a"
                            fill={GraphConstants.COLORS[brand].secondary}
                        />
                    )}
                    {rocketshipTotal && (
                        <Bar
                            dataKey={barThreeKey}
                            stackId="a"
                            fill={GraphConstants.COLORS[brand].tertiary}
                        />
                    )}
                </BarChart>
            </ResponsiveContainer>
        </div>
    );

    const number = coreTotal + boostTotal + rocketshipTotal;
    const title = `${configs.TITLE_PREFIX}: ${numberUtils.asCurrencyWithDefault(number, '-')}`;

    return (
        <div className="Graph">
            <GraphTitle
                title={title}
                isLoading={isLoading}
            />
            {isLoading ? <SpinnerContainer customClass="Graph-Spinner" /> : renderGraph()}
        </div>
    );
};

InvoiceGraph.propTypes = propTypes;
InvoiceGraph.defaultProps = defaultProps;

export default InvoiceGraph;
